<template>
  <modal
    size="md"
    ref="modal"
    :show="show"
    @hidden="$emit('input', false)"
    @submit="addInitiative"
    :scrollable="false"
    :hide-footer="!!createdInitiative"
    :title="createdInitiative ? 'Initiative was added' : 'Add a new Initiative'"
    submit-button-text="Add Initiative"
    :is-submitting="isCreatingInitiative"
  >
    <div>
      <a href="javascript:void(0)" ref="anchor" tabindex="-1"></a>
      <div v-if="createdInitiative">
        <div class="d-flex">
          <initiative-icon
            class="ini-icon"
            :is-done-and-closed="createdInitiative.isDoneAndClosed"
          />
          <span class="text-normal-1" v-no-capture>{{ createdInitiative.title }}</span>
        </div>
        <div class="d-flex mt-2 ml-2">
          <b-button class="text-medium-1" variant="link" @click="viewInitiative"
            >View Initiative
          </b-button>
          <b-button class="text-medium-1" variant="link" @click="addAnotherInitiative"
            >Add another one
          </b-button>
        </div>
      </div>
      <div v-else>
        <b-form-textarea
          @change="isTitleFieldErrored = !$event"
          @keydown.esc.stop="focusModalContent"
          @keydown.enter.prevent
          class="title-box"
          :class="{ errored: isTitleFieldErrored || titleFieldErrorMessage }"
          placeholder="Add an initiative"
          row="4"
          v-model="title"
          autofocus
          no-resize
          v-no-capture
        ></b-form-textarea>
        <span class="error-message text-small" v-if="titleFieldErrorMessage">{{
          titleFieldErrorMessage
        }}</span>
        <single-select
          :class="{ errored: isKrFieldErrored }"
          :options="krs"
          :value="kr"
          :loading="isLoadingKrs"
          :menu-max-width="400"
          search-type="dynamic"
          no-result-message="No results found"
          justify-menu="start"
          @select="setKR($event)"
          @search="fetchKRs"
          placeholder="Search and select a key result"
          class="mt-3"
          v-if="!isProjectScopedInitiative"
        >
          <template v-slot:default="item">
            <div class="d-flex align-items-center overflow-hidden">
              <i class="kr-icon mr-2 flex-shrink-0"></i>
              <span class="text-truncate" :title="item.value.title">{{ item.value.title }}</span>
            </div>
          </template>
          <template v-slot:listItem="option">
            <div class="mb-2 overflow-hidden px-1">
              <div class="d-flex align-items-center">
                <i class="goal-icon-greyed flex-shrink-0"></i>
                <span class="pl-2 text-truncate text-secondary text-small-1">{{
                  getGoalTitle(option.option.goalId)
                }}</span>
              </div>
              <div class="d-flex align-items-center">
                <i class="kr-icon flex-shrink-0"></i>
                <span class="pl-2 text-truncate text-medium-1" v-no-capture>{{
                  option.option.title
                }}</span>
              </div>
            </div>
          </template>
        </single-select>
        <max-initiatives-paywall-modal
          v-model="isPaywallModalShown"
          :org-id="orgId"
          v-if="isPaywallModalShown"
        />
      </div>
    </div>
  </modal>
</template>

<script>
import SingleSelect from '@/components/form/SingleSelect'
import InitiativeIcon from '@/components/initiative/InitiativeIcon'
import Modal from '@/components/Modal'
import MaxInitiativesPaywallModal from '@/components/paywalls/MaxInitiativesPaywallModal.vue'
import errorCodes from '@/constants/errorCodes'
import { featureFlags } from '@/constants/featureFlags'
import EventBusMixin from '@/mixins/EventBusMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import AbortableAPIClient from '@/services/abortableAPIClient'
import urlManager from '@/urlManager'
import handleClickNavigation from '@/utils/handleClickNavigation'
import getIdFromCFPrefixedField from '@/utils/initiative/getIdFromCFPrefixedField'
import getNewInitiativePayload from '@/utils/initiative/getNewInitiativePayload'
import isFieldACustomField from '@/utils/initiative/IsFieldACustomField'
import { getInitiativePageRouteConfig } from '@/utils/navigationConfigs'
import showAPIError from '@/utils/showAPIError'
import { BButton, BFormTextarea } from 'bootstrap-vue'
import * as R from 'ramda'
import { mapActions } from 'vuex'

/**
 * This gives a modal containing a form which lets user create an initiative quickly
 */
export default {
  name: 'QuickAddModal',
  props: ['orgId', 'show', 'payload'],
  mixins: [EventBusMixin, FeaturesMixin],
  model: {
    prop: 'show'
  },
  components: {
    BFormTextarea,
    BButton,
    SingleSelect,
    InitiativeIcon,
    Modal,
    MaxInitiativesPaywallModal
  },
  data() {
    return {
      title: '',
      krs: null,
      kr: null,
      isLoadingKrs: false,
      isCreatingInitiative: false,
      abortableAPIClient: new AbortableAPIClient(),
      createdInitiative: null,
      isShown: true,
      isKrFieldErrored: false,
      isTitleFieldErrored: false,
      titleFieldErrorMessage: null,
      isPaywallModalShown: false,
      isInitiativeAdditionAllowed: true
    }
  },
  computed: {
    isProjectScopedInitiative() {
      return !!this.payload?.projectId
    }
  },
  methods: {
    ...mapActions(['getGoal', 'createInitiative']),
    onHide() {
      this.isShown = false
      this.refresh()
    },
    async onShown() {
      this.isShown = true
      await this.setIsInitiativeAdditionAllowed()
      if (!this.isInitiativeAdditionAllowed) {
        this.showPaywall()
      }
    },
    setKR(kr) {
      this.kr = kr
      this.isKrFieldErrored = false
    },
    getGoalTitle(goalId) {
      return this.$store.getters.goalById(goalId)?.title
    },
    async fetchKRs(search) {
      try {
        this.isLoadingKrs = true
        const { data: krs } = await this.abortableAPIClient.get(urlManager.keyResults(this.orgId), {
          params: {
            search,
            is_archived: false,
            ...this.payload?.krsQuery
          }
        })
        this.krs = krs
          .map(kr => ({ ...kr, label: kr.title }))
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      } catch (e) {
        if (e.code !== 'ERR_CANCELED') {
          showAPIError(e)
        }
      } finally {
        this.isLoadingKrs = false
      }
    },
    handleError(e) {
      this.titleFieldErrorMessage = R.path(['response', 'data', 'detail', 'title', '0'], e)
      const isLimitReached = R.pathEq(
        ['response', 'data', 'code'],
        errorCodes.maxInitiativeCountLimitReached,
        e
      )
      if (isLimitReached) {
        this.showPaywall()
      } else {
        this.isTitleFieldErrored = !!this.titleFieldErrorMessage
        !this.titleFieldErrorMessage && showAPIError(e, ['detail', 'title', '0'])
      }
    },
    async setCustomField(initiative) {
      try {
        const initiativeField = this.payload?.fieldName
        const initiativeFieldValue = this.payload?.fieldValue
        if (isFieldACustomField(initiativeField) && initiativeFieldValue) {
          const orgId = initiative.organizationId
          const fieldId = getIdFromCFPrefixedField(initiativeField)
          const fields = this.$store.getters.customFieldDefinitions(orgId)
          const field = fields.find(field => field.id === fieldId)
          await this.$store.dispatch('setInitiativeCustomField', {
            orgId: orgId,
            initiativeId: initiative.id,
            field: field,
            value: initiativeFieldValue
          })
        }
      } catch (e) {
        console.warn('Error while setting custom field')
      }
    },
    async addInitiative() {
      if (!this.isInitiativeAdditionAllowed) {
        this.showPaywall()
        return
      }
      this.isTitleFieldErrored = !this.title
      this.titleFieldErrorMessage = ''
      this.isKrFieldErrored = !this.isProjectScopedInitiative && !this.kr
      if (this.isTitleFieldErrored || this.isKrFieldErrored) {
        return
      }
      const payload = this.payload || {}
      const initiative = getNewInitiativePayload(this.kr, this.title, this.orgId, payload.projectId)
      const fieldName = payload.fieldName
      if (fieldName) {
        if (fieldName === 'owner') {
          initiative.ownerId = payload.fieldValue
        } else if (!isFieldACustomField(fieldName)) {
          initiative[fieldName] = payload.fieldValue
        }
      }
      try {
        this.isCreatingInitiative = true
        this.createdInitiative = await this.createInitiative(initiative)
        this.title = ''
        await this.setCustomField(this.createdInitiative)
        this.focusModalContent()
        payload.onCreate?.()
      } catch (e) {
        this.handleError(e)
      } finally {
        this.isCreatingInitiative = false
      }
    },
    viewInitiative(event) {
      const location = getInitiativePageRouteConfig({
        orgId: this.createdInitiative.organizationId,
        initiativeId: this.createdInitiative.id
      })
      handleClickNavigation.call(this, event, location)
      this.closeModal()
    },
    addAnotherInitiative() {
      this.refresh()
    },
    refresh() {
      this.createdInitiative = null
    },
    closeModal() {
      this.$refs.modal.hide()
    },
    focusModalContent() {
      this.$refs.anchor.focus() // this is to make sure that modal is in focus to get it to close on esc
    },
    async setIsInitiativeAdditionAllowed() {
      this.isInitiativeAdditionAllowed = await this.satisfyFeature(
        this.orgId,
        featureFlags.MAX_INITIATIVE_COUNT
      )
    },
    showPaywall() {
      this.isPaywallModalShown = true
    }
  },
  async mounted() {
    this.listenToEventBus('SUBMIT_FORM', () => {
      this.isShown && this.addInitiative()
    })
    !this.krs && this.fetchKRs('')
    if (this.show) {
      this.onShown()
    }
  },
  watch: {
    krs() {
      this.krs.forEach(({ goalId }) => {
        this.getGoal({
          orgId: this.orgId,
          goalId
        })
      })
    },
    show() {
      if (this.show) {
        this.onShown()
      } else {
        this.onHide()
      }
    },
    orgId() {
      this.fetchKRs('')
      this.kr = null
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.title-box {
  background: $quinary;
  padding: 12px;
  border-radius: 10px;
  min-height: 100px;
  box-shadow: $fieldInsetShadow;
  border: 1px solid transparent;
  transition: all ease-in 200ms;

  &:focus {
    outline: none;
    border-color: $brand;
  }

  &.errored {
    border-color: $error;
  }
}

/deep/ .errored .field {
  border-color: $error;
}

.error-message {
  color: $error;
  padding-left: 12px;
}

.goal-icon-greyed {
  @include dimension(13px, 13px);
  background-size: contain;
}

.kr-icon {
  @include dimension(13px, 14px);
}

.ini-icon {
  margin-top: 2px;
}

.close-rounded-icon {
  position: absolute;
  right: 12px;
  top: 12px;
}
</style>
